import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  type EducationPlanSchedule,
  getConfigurationScheduleStatus,
  getScheduleConfigurationTimetables,
  generateScheduleConfiguration,
  getScheduleConfigurationById,
} from 'services/api';
import { getScheduleUrl, type ConfigurationSchedulesPageParams } from 'services/navigation';
import type { CurriculumScheduleStatus, ScheduleConfiguration } from 'services/api';
import type { AppTableProps } from 'shared/UI/UIKit/Table';
import { getColumns } from './columns';

export const useEducationPlanSchedules = () => {
  const { configurationId } = useParams<ConfigurationSchedulesPageParams>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [schedules, setSchedules] = useState<EducationPlanSchedule[]>([]);
  const [scheduleStatus, setScheduleStatus] = useState<CurriculumScheduleStatus | null>(null);
  const [configuration, setConfiguration] = useState<ScheduleConfiguration | null>(null);

  useEffect(() => {
    if (configurationId) {
      getScheduleConfigurationById(Number(configurationId)).then((res) => {
        setConfiguration(res);
      });
    }
  }, [configurationId]);

  const getTableItems = async () => {
    setLoading(true);

    if (!configurationId) return;

    await getScheduleConfigurationTimetables(configurationId)
      .then((res) => {
        setSchedules(res);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getScheduleStatus = async () => {
    if (!configurationId) return;

    await getConfigurationScheduleStatus(configurationId).then((res) => {
      setScheduleStatus(res);
    });
  };

  const handleGenerateSchedule = async () => {
    if (!configurationId) return;

    await generateScheduleConfiguration(configurationId).then(() => handleUpdateSchedule());
  };

  const handleUpdateSchedule = () => {
    getTableItems();
    getScheduleStatus();
  };

  const onRowHandleClick: AppTableProps<EducationPlanSchedule>['onRow'] = (data) => {
    return {
      onClick: () => {
        navigate(
          getScheduleUrl({ configurationId: configurationId!, scheduleId: data.id.toString() }),
        );
      },
    };
  };

  useEffect(() => {
    handleUpdateSchedule();
  }, []);

  return {
    state: {
      configuration,
      dataSource: schedules,
      columns: getColumns(),
      loading,
      scheduleStatus,
    },
    methods: {
      onRowHandleClick,
      handleGenerateSchedule,
      handleUpdateSchedule,
    },
  };
};
